import { hasEntitySummaryTab } from '@revelio/auth';
import { PagePaths } from '@revelio/core';
import { Tab } from '@revelio/data-access';

import AnalyticsIcon from './AnalyticsIcon';
import ApplicationsIcon from './ApplicationsIcon';
import CompanyIcon from './CompanyIcon';
import DiscoveryIcon from './DiscoveryIcon';
import GeographyIcon from './GeographyIcon';
import IndividualIcon from './IndividualIcon';
import type { PageCardInterface } from './PageCard';
import ReportsIcon from './ReportsIcon';
import RoleIcon from './RoleIcon';

export interface PageCardConfig extends PageCardInterface {
  /** Returns true if the card should be visible for the given user tabs. */
  isVisible: (userTabs: Tab[]) => boolean;
}

const hasAny = (userTabs: Tab[], allowed: Tab[]) =>
  allowed.some((t) => userTabs.includes(t));

// Analytics tabs in priority order (highest first). The Analytics card uses
// this list to gate visibility and to fall through to the first page the user
// can access when they lack summary page access.
const ANALYTICS_TAB_TO_PATH: ReadonlyArray<readonly [Tab, PagePaths]> = [
  [Tab.Overview, PagePaths.COMPANY_COMPOSITIONS],
  [Tab.Transition, PagePaths.COMPANY_TRANSITIONS],
  [Tab.Posting, PagePaths.COMPANY_POSTINGS],
  [Tab.Compensation, PagePaths.COMPANY_PAY],
  [Tab.Sentiment, PagePaths.COMPANY_SENTIMENT],
];
const ANALYTICS_TABS: Tab[] = ANALYTICS_TAB_TO_PATH.map(([tab]) => tab);

const resolveAnalyticsPath = (userTabs: Tab[]): string => {
  if (hasEntitySummaryTab(userTabs)) {
    return `/${PagePaths.COMPANY_SUMMARY}`;
  }
  const match = ANALYTICS_TAB_TO_PATH.find(([tab]) => userTabs.includes(tab));
  return `/${match?.[1] ?? PagePaths.COMPANY_COMPOSITIONS}`;
};

const hasAnalyticsAccess = (userTabs: Tab[]) =>
  hasEntitySummaryTab(userTabs) || hasAny(userTabs, ANALYTICS_TABS);

// Tabs that grant access to any page in the Applications product area.
const APPLICATIONS_TABS: Tab[] = [Tab.DataEnrichment, Tab.ResumeParsing];

const resolveApplicationsPath = (userTabs: Tab[]): string =>
  userTabs.includes(Tab.DataEnrichment)
    ? `/${PagePaths.DELIVERABLES}/${PagePaths.DATA_ENRICHMENT}`
    : `/${PagePaths.RESUME_ENRICHMENT}`;

// Tabs that grant access to the old-layout entity cards (Companies, Cities, Jobs).
const ENTITY_CARD_TABS: Tab[] = [Tab.EntitySummary, Tab.Overview];

const entitySummaryCards: PageCardConfig[] = [
  {
    heading: 'Companies & <br class="landing-break-line"/>Industries',
    description:
      'Track the headcounts, inflows, and outflows of any role at any company or industry to see real time changes in the workforce',
    bgColor: '#F6FAFF',
    hoverBgColor: '#E4F1FF',
    path: `/${PagePaths.COMPANY_SUMMARY}`,
    testId: 'landing-page-card-company',
    icon: <CompanyIcon />,
    isVisible: (userTabs) => hasAny(userTabs, ENTITY_CARD_TABS),
  },
  {
    heading: 'Cities & <br class="landing-break-line"/>States',
    description:
      'See the workforce at any city and state to understand changes in compensation and enhance your talent intelligence capabilities',
    bgColor: '#F3FDF7',
    hoverBgColor: '#DDF8E8',
    path: `/${PagePaths.GEOGRAPHY_SUMMARY}`,
    testId: 'landing-page-card-geography',
    icon: <GeographyIcon />,
    isVisible: (userTabs) => hasAny(userTabs, ENTITY_CARD_TABS),
  },
  {
    heading: 'Jobs & <br class="landing-break-line"/>Skills',
    description:
      'Analyze how technology is impacting the workforce, identify skill gaps, and how the occupational landscape is transforming',
    bgColor: '#F9F6FF',
    hoverBgColor: '#EDE5FF',
    path: `/${PagePaths.ROLE_SUMMARY}`,
    testId: 'landing-page-card-role',
    icon: <RoleIcon />,
    isVisible: (userTabs) => hasAny(userTabs, ENTITY_CARD_TABS),
  },
];

const talentDiscoveryCard: PageCardConfig = {
  heading: 'Talent & <br class="landing-break-line"/>Prospects',
  description:
    'Access the most powerful talent discovery tool, allowing anybody to find professionals, to either partner with or recruit',
  bgColor: '#FFF9F7',
  hoverBgColor: '#FFEDE7',
  path: `/${PagePaths.TALENT_DISCOVERY}`,
  testId: 'landing-page-card-talent',
  icon: <IndividualIcon />,
  isVisible: (userTabs) => userTabs.includes(Tab.TalentDiscovery),
};

export const getPageCardData = (
  hasEntitySummary: boolean
): PageCardConfig[] => {
  const resolvedEntityCards = hasEntitySummary
    ? entitySummaryCards
    : entitySummaryCards.map((card) => ({
        ...card,
        path: card.path.replace('/summary', '/compositions'),
      }));

  return [...resolvedEntityCards, talentDiscoveryCard];
};

const NEW_LAYOUT_CARD_MIN_H = '250px';

// Card definitions extracted as module-level constants so the SVG icon JSX
// is instantiated once and reused across renders.
const analyticsCardBase: Omit<PageCardConfig, 'path'> = {
  heading: 'Analytics',
  description: 'Real-time workforce intelligence and labor market data',
  bgColor: '#F6FAFF',
  hoverBgColor: '#E4F1FF',
  testId: 'landing-page-card-analytics',
  icon: <AnalyticsIcon />,
  isVisible: hasAnalyticsAccess,
  minH: NEW_LAYOUT_CARD_MIN_H,
};

const discoveryCard: PageCardConfig = {
  heading: 'Discovery',
  description: 'Find individuals, companies, geographies, and roles',
  bgColor: '#F3FDF7',
  hoverBgColor: '#DDF8E8',
  path: `/${PagePaths.TALENT_DISCOVERY}`,
  testId: 'landing-page-card-discovery',
  icon: <DiscoveryIcon />,
  isVisible: (userTabs) => userTabs.includes(Tab.TalentDiscovery),
  minH: NEW_LAYOUT_CARD_MIN_H,
};

const applicationsCardBase: Omit<PageCardConfig, 'path'> = {
  heading: 'Applications',
  description: 'Tools to support decision making and workflows',
  bgColor: '#F9F6FF',
  hoverBgColor: '#EDE5FF',
  testId: 'landing-page-card-applications',
  icon: <ApplicationsIcon />,
  isVisible: (userTabs) => hasAny(userTabs, APPLICATIONS_TABS),
  minH: NEW_LAYOUT_CARD_MIN_H,
};

const reportsCard: PageCardConfig = {
  heading: 'Reports',
  description: 'Tailored reports for deeper workforce insights',
  bgColor: '#FFF9F7',
  hoverBgColor: '#FFEDE7',
  path: `/${PagePaths.REPORT_BUILDER}`,
  testId: 'landing-page-card-reports',
  icon: <ReportsIcon />,
  isVisible: (userTabs) => userTabs.includes(Tab.Reports),
  minH: NEW_LAYOUT_CARD_MIN_H,
};

export const getNewLayoutPageCardData = (userTabs: Tab[]): PageCardConfig[] => [
  { ...analyticsCardBase, path: resolveAnalyticsPath(userTabs) },
  discoveryCard,
  { ...applicationsCardBase, path: resolveApplicationsPath(userTabs) },
  reportsCard,
];
